import { getTenant } from '../../../theme/tokens'

export type ShiftStatus =
  | 'open'
  | 'closed'
  | 'variance'
  | 'pending_settlement'
  | 'settled'

export type ShiftTxnMethod = 'card' | 'qr' | 'cash'

export type ShiftTxnStatus = 'approved' | 'refunded' | 'voided'

export interface ShiftTransaction {
  id: string
  time: string
  method: ShiftTxnMethod
  amount: number
  status: ShiftTxnStatus
  reference: string
}

export interface Shift {
  id: string
  status: ShiftStatus
  store: string
  terminalId: string
  operator: string
  openedAt: string
  closedAt: string | null
  duration: string
  txnCounts: number
  grossValue: number
  netValue: number
  variance: number | null
  openingFloat: number
  refundsValue: number
  refundsCount: number
  payIns: number
  payOuts: number
  transactions: ShiftTransaction[]
}

const METHODS: ShiftTxnMethod[] = ['card', 'qr', 'cash']

// Deterministic per-shift transaction sample so the detail view can list each
// individual transaction without relying on random values across renders.
function buildTransactions(
  shiftId: string,
  openedAt: string,
  count: number,
  refundsCount: number,
): ShiftTransaction[] {
  const start = new Date(openedAt).getTime()
  const seq = Math.min(count, 24)
  const txns: ShiftTransaction[] = []
  for (let i = 0; i < seq; i++) {
    const method = METHODS[i % METHODS.length]
    const minutes = Math.round((i + 1) * 11.5)
    const time = new Date(start + minutes * 60_000).toISOString()
    const base = 12 + ((i * 37) % 240)
    const amount = Number((base + (i % 5) * 1.55).toFixed(2))
    const isRefund = i > 0 && i % Math.max(6, seq - refundsCount) === 0
    const isVoid = i === 4
    const status: ShiftTxnStatus = isRefund
      ? 'refunded'
      : isVoid
        ? 'voided'
        : 'approved'
    txns.push({
      id: `${shiftId}-T${String(i + 1).padStart(3, '0')}`,
      time,
      method,
      amount,
      status,
      reference:
        method === 'cash' ? '—' : `AUTH${String(100000 + i * 731).slice(0, 6)}`,
    })
  }
  return txns
}

const baseShifts: Omit<Shift, 'transactions'>[] = [
  {
    id: 'SHF-20260506-001',
    status: 'open',
    store: 'Bila Bila Mart — KLCC',
    terminalId: 'TERM-KLCC-01',
    operator: 'Siti Nurhaliza',
    openedAt: '2026-05-06T08:00:00Z',
    closedAt: null,
    duration: '4h 15m',
    txnCounts: 142,
    grossValue: 4250.5,
    netValue: 4100.0,
    variance: null,
    openingFloat: 500,
    refundsValue: 150.5,
    refundsCount: 3,
    payIns: 0,
    payOuts: 0,
  },
  {
    id: 'SHF-20260505-004',
    status: 'variance',
    store: 'Bila Bila Mart — Bangsar',
    terminalId: 'TERM-BANG-02',
    operator: 'Wei Ling',
    openedAt: '2026-05-05T16:00:00Z',
    closedAt: '2026-05-06T00:00:00Z',
    duration: '8h 0m',
    txnCounts: 284,
    grossValue: 8420.0,
    netValue: 8350.0,
    variance: -15.5,
    openingFloat: 500,
    refundsValue: 70.0,
    refundsCount: 2,
    payIns: 100,
    payOuts: 50,
  },
  {
    id: 'SHF-20260505-003',
    status: 'pending_settlement',
    store: 'Bila Bila Mart — KLCC',
    terminalId: 'TERM-KLCC-01',
    operator: 'Muthu Kumar',
    openedAt: '2026-05-05T08:00:00Z',
    closedAt: '2026-05-05T16:00:00Z',
    duration: '8h 0m',
    txnCounts: 315,
    grossValue: 9150.75,
    netValue: 9000.75,
    variance: 0,
    openingFloat: 500,
    refundsValue: 150.0,
    refundsCount: 4,
    payIns: 0,
    payOuts: 0,
  },
  {
    id: 'SHF-20260504-002',
    status: 'settled',
    store: 'Bila Bila Mart — Mid Valley',
    terminalId: 'TERM-MID-01',
    operator: 'Nurul Amin',
    openedAt: '2026-05-04T08:00:00Z',
    closedAt: '2026-05-04T16:00:00Z',
    duration: '8h 0m',
    txnCounts: 412,
    grossValue: 12450.0,
    netValue: 12200.0,
    variance: 5.0,
    openingFloat: 500,
    refundsValue: 250.0,
    refundsCount: 5,
    payIns: 0,
    payOuts: 0,
  },
]

export const mockShifts: Shift[] = baseShifts.map((s) => ({
  ...s,
  transactions: buildTransactions(
    s.id,
    s.openedAt,
    s.txnCounts,
    s.refundsCount,
  ),
}))
